// http://stackoverflow.com/questions/1303646
import {isString} from "./string";

export function isNumber(o) {
    return !isNaN(o - 0) && o !== null && o !== "" && o !== false;
}

/**
 * Returns a random integer between min (inclusive) and max (inclusive).
 * The value is no lower than min (or the next integer greater than min
 * if min isn't an integer) and no greater than max (or the next integer
 * lower than max if max isn't an integer).
 * Using Math.round() will give you a non-uniform distribution!
 * https://stackoverflow.com/a/1527820/19588
 */
export function getRandomInt(min, max) {
    min = Math.ceil(min);
    max = Math.floor(max);
    return Math.floor(Math.random() * (max - min + 1)) + min;
}

/**
 * @link https://stackoverflow.com/a/64876394
 * @param value that we want to find the number of decimal places on.
 * @returns {number|number} the number of decimal places in the passed in value.
 */
export function decimalCount(value) {
    let num = parseFloat(value); // First convert to number to check if whole

    if (Number.isInteger(num) === true) return 0;

    let text = num.toString(); // Convert back to string and check for "1e-8" numbers

    if (text.indexOf('e-') > -1) {
        let [base, trail] = text.split('e-');
        let deg = parseInt(trail, 10);
        return deg;
    } else {
        let index = text.indexOf(".");
        return text.length - index - 1; // Otherwise use simple string function to count
    }
}


/**
 * Attempts to parse the given value into a float number.
 * @param {*} value - The value to be parsed.
 * @returns {number|undefined} The parsed float number, or undefined if the value can't be parsed.
 */
export function parseFloatNumber(value) {
    try {
        const floatNumber = parseFloat(value);
        if (isNaN(floatNumber)) return undefined;
        return floatNumber;
    } catch (err) {
        return undefined;
    }
}


// English number formatting values…
export const NUMBER_FORMAT = {delimiter: ",", separator: "."};

// English percentage formatting values…
export const NUMBER_PERCENTAGE_FORMAT = {
    delimiter: ",",
    separator: ".",
    precision: 1,
    stripInsignificantZeros: false,
};

// English precision formatting values…
export const NUMBER_PRECISION_FORMAT = {
    delimiter: ",",
    separator: ".",
    precision: 2,
    stripInsignificantZeros: true,
};

// English currency formatting values…
export const NUMBER_CURRENCY_FORMAT = {
    unit: "$",
    format: "%u%n",
    separator: ".",
    delimiter: ",",
    precision: 2
};


/**
 * Formats a number as a percentage string (e.g., 65%). You can customize the
 * format in the options object.
 *
 * @param {number} number - The number to format as a percentage.
 * @param {object} [options={}] - Options to customize the formatting.
 * @param {number} [options.precision] - The number of decimal places.
 * @param {string} [options.separator] - The separator between the units.
 * @param {string} [options.delimiter] - The thousands delimiter.
 * @returns {string} - The formatted percentage string.
 */
export function numberToPercentage(number, options = {}) {
    const defaults = {...NUMBER_FORMAT, ...NUMBER_PERCENTAGE_FORMAT};

    const precision = isNumber(options.precision) ? options.precision : defaults.precision;
    const separator = isString(options.separator) ? options.separator : defaults.separator;
    const delimiter = isString(options.delimiter) ? options.delimiter : defaults.delimiter;

    try {
        return (
            numberWithPrecision(number, {
                precision: precision,
                separator: separator,
                delimiter: delimiter,
            }) + "%"
        );
    } catch {
        return number;
    }
}

/**
 * Formats a number with the specified level of precision (e.g., 112.32 has a precision of 2).
 * You can customize the format in the options object.
 *
 * @param {number} number - The number to format with the specified precision.
 * @param {object} [options={}] - Options to customize the formatting.
 * @param {number} [options.precision] - The number of decimal places.
 * @param {string} [options.separator] - The separator between the units.
 * @param {string} [options.delimiter] - The thousands delimiter.
 * @returns {string} - The formatted number with the specified precision.
 */
export function numberWithPrecision(number, options = {}) {
    const defaults = {...NUMBER_FORMAT, ...NUMBER_PRECISION_FORMAT};

    const precision = isNumber(options.precision) ? options.precision : defaults.precision;
    const separator = isString(options.separator) ? options.separator : defaults.separator;
    const delimiter = isString(options.delimiter) ? options.delimiter : defaults.delimiter;

    try {
        const roundedNumber = Math.round(number * 10 ** precision) / 10 ** precision;
        return numberWithDelimiter(roundedNumber.toFixed(precision), {
            separator: separator,
            delimiter: delimiter,
        });
    } catch {
        return number;
    }
}

/**
 * Formats a number with grouped thousands using a delimiter (e.g., 12,324).
 * You can customize the format in the options object.
 *
 * @param {number} number - The number to format with grouped thousands.
 * @param {object} [options={}] - Options to customize the formatting.
 * @param {string} [options.delimiter] - The thousands delimiter.
 * @param {string} [options.separator] - The separator between the units.
 * @returns {string} - The formatted number with grouped thousands.
 */
export function numberWithDelimiter(number, options = {}) {
    const defaults = NUMBER_FORMAT;

    const delimiter = isString(options.delimiter) ? options.delimiter : defaults.delimiter;
    const separator = isString(options.separator) ? options.separator : defaults.separator;

    try {
        const parts = number.toString().split(".");
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, delimiter);
        return parts.join(separator);
    } catch {
        return number;
    }
}


/**
 * Formats a number into a currency string (e.g., $13.65). You can customize the format
 * in the options object.
 *
 * @param {number} number - The number to be formatted as currency.
 * @param {Object} [options={}] - The formatting options.
 * @param {number} [options.precision=2] - Sets the level of precision.
 * @param {string} [options.unit="$"] - Sets the denomination of the currency.
 * @param {string} [options.separator="."] - Sets the separator between the units.
 * @param {string} [options.delimiter=","] - Sets the thousands delimiter.
 * @param {string} [options.format="%u%n"] - Sets the format of the output string.
 * @returns {string} The formatted currency string.
 *
 * @example
 * numberToCurrency(1234567890.50) // => $1,234,567,890.50
 * numberToCurrency(1234567890.506) // => $1,234,567,890.51
 * numberToCurrency(1234567890.506, { precision: 3 }) // => $1,234,567,890.506
 */
export function numberToCurrency(number, options = {}) {
    const defaults = NUMBER_CURRENCY_FORMAT;

    const precision = isNumber(options.precision) ? options.precision : defaults.precision;
    const delimiter = isString(options.delimiter) ? options.delimiter : defaults.delimiter;
    const separator = isString(options.separator) ? options.separator : defaults.separator;
    const unit = isString(options.unit) ? options.unit : defaults.unit;
    const format = isString(options.format) ? options.format : defaults.format;

    const formattedNumber = numberWithPrecision(number, {
        precision,
        delimiter,
        separator,
    });

    const formattedCurrency = format
        .replace("%n", formattedNumber)
        .replace("%u", unit);

    return formattedCurrency;
}
